import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, MessageSquarePlus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MachineSuggestions = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    location: "",
    suggestions: "",
    contact_name: "",
    contact_email: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/suggestions`, formData);
      toast.success("Thanks for your suggestion! We'll review it soon.");
      setFormData({
        location: "",
        suggestions: "",
        contact_name: "",
        contact_email: "",
      });
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="suggestions-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-navy relative overflow-hidden" data-testid="suggestions-hero">
        <div className="absolute top-10 right-10 w-8 h-8 bg-gold rotate-12 opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        <div className="absolute bottom-20 left-20 w-10 h-10 bg-coral opacity-40" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
            FOR EXISTING CLIENTS
          </span>
          <h1 className="font-heading text-4xl md:text-5xl text-cream mb-4">
            Suggest New <span className="text-coral">Snacks & Drinks</span>
          </h1>
          <p className="text-cream/80 max-w-2xl mx-auto text-lg">
            Have a favorite snack or drink you'd like to see in your machine? 
            Let us know and we'll do our best to stock it!
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24" data-testid="suggestions-form-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border-2 border-navy p-8 md:p-12 shadow-[12px_12px_0px_0px_#F2CC8F]">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="bg-coral w-14 h-14 rounded-xl flex items-center justify-center">
                <MessageSquarePlus className="h-7 w-7 text-cream" />
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="suggestion-form">
              {/* Location */}
              <div>
                <label className="block font-bold text-navy mb-2" htmlFor="location">
                  Machine Location *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="input-retro w-full"
                  placeholder="e.g., 123 Main St - 2nd Floor Break Room"
                  data-testid="suggestion-location"
                />
                <p className="text-sm text-navy/60 mt-1">
                  Enter the address or building name where your machine is located
                </p>
              </div>

              {/* Suggestions */}
              <div>
                <label className="block font-bold text-navy mb-2" htmlFor="suggestions">
                  Snack & Drink Suggestions *
                </label>
                <Textarea
                  id="suggestions"
                  name="suggestions"
                  value={formData.suggestions}
                  onChange={handleChange}
                  required
                  className="input-retro w-full min-h-[150px]"
                  placeholder="Tell us what you'd like to see! For example:
- More healthy options like KIND bars
- La Croix sparkling water
- Spicy chips (Takis, Hot Cheetos)
- Fresh fruit cups
- Any specific brands or flavors..."
                  data-testid="suggestion-text"
                />
              </div>

              {/* Optional Contact Info */}
              <div className="border-t-2 border-navy/10 pt-6 mt-6">
                <p className="text-sm text-navy/60 mb-4">
                  Optional: Leave your contact info if you'd like updates on your suggestions
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-bold text-navy mb-2" htmlFor="contact_name">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      className="input-retro w-full"
                      placeholder="John Smith"
                      data-testid="suggestion-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block font-bold text-navy mb-2" htmlFor="contact_email">
                      Email
                    </label>
                    <input
                      type="email"
                      id="contact_email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      className="input-retro w-full"
                      placeholder="john@company.com"
                      data-testid="suggestion-email"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="suggestion-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Submit Suggestion
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Info Box */}
          <div className="mt-8 bg-gold/20 rounded-xl border-2 border-navy p-6">
            <h3 className="font-heading text-lg text-navy mb-2">How It Works</h3>
            <ul className="text-navy/70 text-sm space-y-2">
              <li>• We review all suggestions weekly</li>
              <li>• Popular requests get prioritized for restocking</li>
              <li>• We'll try to accommodate requests based on machine capacity</li>
              <li>• Some specialty items may take longer to source</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MachineSuggestions;
